import { isSameDay } from 'date-fns';
import { ProductProps } from '@/types';
import { getOnStorage, setOnStorage } from '@/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetchAxios, useNetworkStatusContext, useTables, useUserContext } from '@/hooks';

export interface OrderProps {
  clientName: string;
  clientRefNumber: string;
  clientCnpj: string;
  orders: OrderItemsProps;
}
export interface OrderItemsProps {
  orderNumber: string;
  erpNumberClient?: string;
  erpNumber: string;
  order_number_origem?: string;
  origin: string;
  orderNumberMagento: string;
  quantity: number | string;
  status: string;
  isLocal?: boolean;
  reason: string;
  orderDate: string;
  total: string;
  vlr_comissao?: number;
  paymentTerms: string;
  actionsPending: ItemsProps[];
}
export interface ItemsProps extends ProductProps {
  cod_segmento: number;
  sku: string;
  product: string;
  price: string;
  unitPrice: string;
  unitPricePerItem: string;
  stPrice: string;
  stPricePackaging: number;
  integrationPrice: number;
  integrationPriceFlex: number;
  quantity: number;
  quantityMultiplo: number;
  percentualFlex?: string;
  packaging: string;
  status: string;
  reason: string;

  unilever?: boolean;
  price_flex?: number;
  st_price_flex?: number;
  unit_price_flex?: number;
  unit_price_per_item_flex?: number;
}

export const useOrderStatusPage = () => {
  const { tables } = useTables();
  const { customer } = useUserContext();
  const isLoadedSynchedOrders = useRef(false);
  const { axiosCtx: axiosInstance } = useFetchAxios();
  const { connectivityStatus } = useNetworkStatusContext();

  const isLoading = useRef(false);
  const [data, setData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [filterDate, setFilterDate] = useState<Date>();
  const [error, setError] = useState<Error | null>(null);
  const [orders, setOrders] = useState<OrderProps[]>([]);
  const [offlineOrders, setOfflineOrders] = useState<OrderProps[]>([]);
  const [products, setProducts] = useState<Record<number, ProductProps>>({});
  const [synchedOrdersList, setSynchedOrdersList] = useState<OrderProps[]>([]);

  const isLoadedProducts = useMemo(() => Object.keys(products || {})?.length, [products]);

  const synchedOrders = getOnStorage('synched_orders') || [];
  const offlineOrdersStr = getOnStorage('offline_orders') || [];

  useEffect(() => {
    if (data?.length && !isLoadedSynchedOrders.current) {
      isLoadedSynchedOrders.current = true;

      // Alguns ambientes retornam `order_number_origem` (pt-BR) e outros `order_number_origin` (en).
      // Tornamos a checagem resiliente a ambos para limpar a lista de sincronizados corretamente.
      const ordersIds = new Set(
        data.map(
          (item: any) => item?.orders?.order_number_origem || item?.orders?.order_number_origin || item?.orders?.order_number
        )
      );
      const newList = synchedOrders?.filter((item: any) => !ordersIds.has(item?.orders?.order_number));
      setOnStorage('synched_orders', newList);
    }
  }, [data]);

  useEffect(() => {
    if (loading || data || isLoadedProducts || isLoading.current) return;

    isLoading.current = true;
    const loggedUser = getOnStorage('user_logado');
    const rca = getOnStorage('selected_rca');

    const fetchData = async () => {
      const clients = tables?.current?.cliente || [];
      let codRcas;

      if (rca != null && rca?.codPessoa !== loggedUser?.codPessoa) {
        codRcas = [rca?.codPessoa];
      } else {
        codRcas = [...new Set((Array.isArray(clients) ? clients : []).map((client: any) => client?.COD_RCA))];
      }

      try {
        setLoading(true);

        const promises = codRcas.map((codRca) =>
          axiosInstance
            .get(`${window.agp_config.REACT_APP_AWS}/pedido-notifica-get?seqpessoa=${codRca}`)
            .then((res) => res.data?.pedidos || [])
            .catch((e) => {
              console.log(`erro ao buscar pedidos para RCA: ${codRca}`);
              return [];
            })
        );

        const allResponses = await Promise.all(promises);
        const mergedPedidos = allResponses.flat();

        const productsDB = tables.current.dicionario_produtos;
        setProducts(productsDB);
        setData(mergedPedidos);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
        isLoading.current = false;
      }
    };

    fetchData();
  }, [axiosInstance, customer.codPessoa, data, loading, isLoadedProducts]);

  const mountOrders = useCallback(
    (orders: any, isLocal?: boolean) => {
      return orders?.map((order: any) => ({
        clientName: order?.company?.name,
        clientRefNumber: order?.company?.id,
        clientCnpj: order?.company?.cnpj,
        orders: {
          isLocal,
          orderNumber: order?.orders?.order_number,
          erpNumber: order?.orders?.erp_order_number,
          orderNumberMagento: order?.orders?.order_number_magento,
          quantity: order?.orders?.items,
          status: order?.orders?.status || 'indisponível',
          reason: order?.orders?.reason,
          orderDate: order?.orders?.order_date,
          total: order?.orders?.total,
          vlr_comissao: order?.orders?.vlr_comissao,
          paymentTerms: order?.orders?.payment_terms,
          actionsPending: order?.orders?.actions_pending?.map((item: any) => ({
            ...item,
            cod_segmento: item?.cod_segmento,
            sku: item?.sku,
            product: item?.product,
            price: item?.price,
            unitPrice: item?.unit_price,
            unitPricePerItem: item?.unit_price_per_item,
            stPrice: item?.st_price,
            stPricePackaging: item?.st_price_packaging,
            integrationPrice: item?.integration_price,
            integrationPriceFlex: item?.integration_price_flex,
            quantity: item?.quantity,
            quantityMultiplo: item?.quantity_multiplo,
            packaging: item?.packaging,
            percentualFlex: item?.percentual_flex,
            status: '',
            reason: '',

            unit_price_flex: item?.unit_price_flex,
            unit_price_per_item_flex: item?.unit_price_per_item_flex,
            st_price_flex: item?.st_price_flex,
            price_flex: item?.price_flex,
            unilever: item?.UNILEVER,

            ...products[item?.sku],
          })),
        },
      }));
    },
    [products]
  );

  useEffect(() => {
    if ((offlineOrders?.length && synchedOrdersList?.length) || !isLoadedProducts) return;

    const fetchOfflineData = () => {
      if (offlineOrdersStr && !offlineOrders?.length) {
        const ordersOffline = mountOrders(offlineOrdersStr, true);
        setOfflineOrders(ordersOffline);
      }

      if (synchedOrders && !synchedOrdersList?.length) {
        const filteredSynchedOrders = synchedOrders?.filter((order: any) => order?.company?.Cod_rca == customer?.codPessoa);
        const synchedList = mountOrders(filteredSynchedOrders, true);
        setSynchedOrdersList(synchedList);
      }
    };

    fetchOfflineData();
  }, [mountOrders, isLoadedProducts]);

  useEffect(() => {
    if (!isLoadedProducts || connectivityStatus === 'offline') return;

    const orders = mountOrders(data);
    setOrders(orders);
  }, [mountOrders, data, isLoadedProducts, connectivityStatus]);

  const ordersDate = useMemo(() => {
    return orders?.map((order: any) => {
      const dateString = order?.orders?.orderDate?.replace(/^[^,]*,\s*/, '');
      return new Date(`${dateString.split('/').reverse().join('-')}T00:00:00`);
    });
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!orders?.length) return [];

    if (!filterDate) return orders;

    return orders.filter((order) => {
      const orderDate = new Date(
        `${order?.orders?.orderDate
          ?.replace(/^[^,]*,\s*/, '')
          .split('/')
          .reverse()
          .join('-')}T00:00:00`
      );
      return isSameDay(orderDate, filterDate);
    });
  }, [orders, filterDate]);

  const filteredSynchedOrdersList = useMemo(() => {
    if (!synchedOrdersList?.length) return [];

    return synchedOrdersList?.filter(
      (syn) => !filteredOrders.find((filOrd) => filOrd.orders.orderNumber.includes(syn.orders.orderNumber))
    );
  }, [synchedOrdersList, filteredOrders]);

  return {
    error,
    loading,
    filterDate,
    ordersDate,
    ordersList: filteredOrders,
    synchedOrdersList: filteredSynchedOrdersList,
    offlineOrdersList: connectivityStatus === 'offline' ? offlineOrders : [],
    setFilterDate,
  };
};
